/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useRecoilState } from "recoil";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { UserAtom } from "../../../atoms/UserAtom";
import { updateUser } from "../../../api/User";
import { useModal } from "../Modal";
import { useEffect } from "react";
import { toast } from "react-toastify";

const ModalBody = styled.div`
  width: 390px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;

  margin: 16px -24px 0px;
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 32px 24px;

  > div + div {
    margin-top: 24px;
  }
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;
`;

const InputLabel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 12px;
`;

export default function UpdateName(): JSX.Element | null {
  const [disable, setDisable] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [user, setUser] = useRecoilState(UserAtom);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const modal = useModal();

  function checkInput(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value.length < 1) setDisable(true);
    else if (disable && event.target.value.length >= 1) setDisable(false);
  }

  async function sendUpdateUser() {
    if (!firstNameRef.current?.value || !lastNameRef.current?.value) {
      toast("Please fill in all the fields", { type: "error" });
      return;
    }

    if (!user) return false;

    setLoading(true);

    const userObject = {
      ...user,
      firstName: firstNameRef.current?.value || "",
      lastName: lastNameRef.current?.value || "",
    };

    delete (userObject as any)._id;

    const success = await updateUser(userObject);

    if (success) {
      setUser({
        ...user,
        firstName: firstNameRef.current?.value || "",
        lastName: lastNameRef.current?.value || "",
      });
      toast("Full name updated", { type: "success" });
      modal.popModal();
    }
    setLoading(false);
    return false;
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        sendUpdateUser();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [user?.firstName, user?.lastName]);

  if (!user) return null;

  return (
    <ModalBody>
      <Wrapper>
        <InputLabel>
          <Title>First Name</Title>
          <StyledInput
            placeholder="Enter first name"
            ref={firstNameRef}
            onChange={checkInput}
            defaultValue={user?.firstName}
          />
        </InputLabel>

        <InputLabel>
          <Title>Last Name</Title>
          <StyledInput
            placeholder="Enter last name"
            ref={lastNameRef}
            onChange={checkInput}
            defaultValue={user?.lastName}
          />
        </InputLabel>
      </Wrapper>

      <Buttons>
        <StyledButton
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
          onClick={() => modal.popModal()}
        >
          Cancel
        </StyledButton>
        <StyledButton
          disable={disable}
          isLoading={isLoading}
          css={css`
            width: 80px;
          `}
          onClick={() => sendUpdateUser()}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
